import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_DRIVER_ACTIVITY_SITE_OPTIONS, ListDriverActivitySiteOptionsResponse } from '../graphql/queries';

export interface SiteSelectOption {
    label: string;
    value: number;
}

export interface SiteGroupOption {
    label: string;
    options: SiteSelectOption[];
}

export interface SiteOptions {
    hubOptions: SiteSelectOption[];
    /** WHS 按 Hub 分组展示，组名用 hubName */
    whsGroupedOptions: SiteGroupOption[];
    /** warehouseId -> hubId，范围冲突判定用 */
    whsHubMap: Record<number, number>;
    /** 任意站点 id -> 名称；不在列表里的 id（后来被拉黑）查不到，调用方按 id 原样显示 */
    siteNameMap: Record<number, string>;
    loading: boolean;
}

/**
 * Hub / WHS 下拉数据：只走 listDriverActivitySiteOptions（后端已按运营配置剔除测试站点黑名单），
 * 不再调 getHubList / getWarehouseListByHubId。
 */
export function useSiteOptions(): SiteOptions {
    const { data, loading } = useQuery<ListDriverActivitySiteOptionsResponse>(LIST_DRIVER_ACTIVITY_SITE_OPTIONS);

    return useMemo(() => {
        const siteNameMap: Record<number, string> = {};
        const whsHubMap: Record<number, number> = {};
        const hubOptions: SiteSelectOption[] = [];
        const whsGroupedOptions: SiteGroupOption[] = [];
        (data?.listDriverActivitySiteOptions ?? []).forEach((hub) => {
            const hubId = Number(hub.hubId);
            siteNameMap[hubId] = hub.hubName;
            hubOptions.push({ label: hub.hubName, value: hubId });
            const options = (hub.warehouses ?? []).map((whs) => {
                const id = Number(whs.id);
                siteNameMap[id] = whs.name;
                whsHubMap[id] = hubId;
                return { label: whs.name, value: id };
            });
            if (options.length) whsGroupedOptions.push({ label: hub.hubName, options });
        });
        return { hubOptions, whsGroupedOptions, whsHubMap, siteNameMap, loading };
    }, [data, loading]);
}
